import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import {
  PERMS_SCHEMA,
  defaultPerms,
  normalizePerms,
  groupPerms,
  applyRequires,
  // ✅ ekle
} from '../../permission/permsSchema';

function applyLinks(next) {
  const out = { ...next };

  // ✅ All => Own
  if (out.canViewMembersAll) out.canViewMembersOwn = true;
  if (out.canEditMembersAll) out.canEditMembersOwn = true;

  if (out.canViewSessionsAll) out.canViewSessionsOwn = true;
  if (out.canEditSessionsAll) out.canEditSessionsOwn = true;

  // ✅ Attendance All => Own
  if (out.canMarkAttendanceAll) out.canMarkAttendanceOwn = true;

  return out;
}

function anyOn(perms, row) {
  return row.scopes.some((s) => !!perms[s.key]);
}

function countOn(perms, row) {
  return row.scopes.filter((s) => !!perms[s.key]).length;
}
function extractPermsFromStaff(staff) {
  // 1) nested permissions varsa onu kullan
  if (staff?.permissions && typeof staff.permissions === 'object') return staff.permissions;

  // 2) yoksa root'tan PERMS_SCHEMA keys ile derle
  const out = {};
  for (const row of PERMS_SCHEMA) {
    for (const sc of row.scopes) {
      out[sc.key] = Boolean(staff?.[sc.key]);
    }
  }
  return out;
}
function buildSubmitPayload(perms) {
  // Backend’in nested beklediği senaryoya uygun:
  return { permissions: perms };

  // Eğer backend flat bekliyorsa alternatif:
  // return { ...perms };
}
export function PermissionsEditModal({ open, staff, onClose, onSubmit, onSaved }) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [perms, setPerms] = React.useState(defaultPerms());
  const [expanded, setExpanded] = React.useState({}); // baseKey -> boolean
  const [err, setErr] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const grouped = React.useMemo(() => groupPerms(PERMS_SCHEMA), [PERMS_SCHEMA]);

  React.useEffect(() => {
    if (!open) return;
    setErr(null);
    setLoading(false);
    setExpanded({}); // her açılışta kapalı başlasın
    const raw = extractPermsFromStaff(staff);
    setPerms(applyLinks(normalizePerms(raw)));
  }, [open, staff]);

  const toggleRow = (baseKey) => setExpanded((s) => ({ ...s, [baseKey]: !s[baseKey] }));

  const setPerm = (k, v) => setPerms((s) => applyLinks(applyRequires(s, k, v, PERMS_SCHEMA)));
  const setRowEnabled = (row, enabled) => {
    setPerms((s) => {
      let next = { ...s };

      if (!enabled) {
        row.scopes.forEach((sc) => {
          next = applyRequires(next, sc.key, false, PERMS_SCHEMA);
        });
        return applyLinks(next);
      }

      // enabled=true
      row.scopes.forEach((sc) => {
        const shouldOn = !!sc.default;
        if (shouldOn) next = applyRequires(next, sc.key, true, PERMS_SCHEMA);
      });

      // view rowlarında en az Own açık kalsın (senin UX kuralın)
      if (row.baseKey === 'membersView')
        next = applyRequires(next, 'canViewMembersOwn', true, PERMS_SCHEMA);
      if (row.baseKey === 'sessionsView')
        next = applyRequires(next, 'canViewSessionsOwn', true, PERMS_SCHEMA);
      if (row.baseKey === 'dashboard')
        next = applyRequires(next, 'canViewDashboardOwn', true, PERMS_SCHEMA);

      return applyLinks(next);
    });
  };

  const isDirty = React.useMemo(() => {
    if (!open) return false;
    const baseRaw = extractPermsFromStaff(staff);
    const base = applyLinks(normalizePerms(baseRaw));
    const keys = Object.keys(defaultPerms());
    return keys.some((k) => !!perms[k] !== !!base[k]);
  }, [open, staff, perms]);

  const saveBlockReason = React.useMemo(() => {
    if (loading) return 'İşlem devam ediyor…';
    if (!isDirty) return 'Değişiklik yok.';
    return null;
  }, [loading, isDirty]);

  const saveDisabled = Boolean(saveBlockReason);

  const submit = async () => {
    setErr(null);
    try {
      setLoading(true);

      console.log('🟣 [PermissionsEditModal.submit] sending perms:', perms);

      await onSubmit?.(buildSubmitPayload(perms));
      await onSaved?.(); // ✅ staff listesi tekrar çekilsin
      onClose?.();
    } catch (e) {
      setErr(e?.message || 'Yetkiler güncellenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const headerBg = 'linear-gradient(180deg, rgba(46,204,113,.16), rgba(46,204,113,.06))';
  const greenSwitchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#16a34a', // green-600
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#16a34a',
      opacity: 0.55,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '22px',
          border: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          boxShadow: theme.shadows[12],
        },
      }}
    >
      <DialogTitle sx={{ p: 0, background: headerBg }}>
        <MDBox sx={{ px: 2, py: 1.5 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDBox
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '16px',
                  display: 'grid',
                  placeItems: 'center',
                  background: 'rgba(46,204,113,.14)',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Icon sx={{ fontSize: 22 }}>shield</Icon>
              </MDBox>

              <MDBox>
                <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                  Yetkileri Düzenle
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                  {staff?.fullName ? `${staff.fullName} için panel izinleri` : 'Panel izinleri'}
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDButton
              variant="outlined"
              color="dark"
              onClick={onClose}
              disabled={loading}
              sx={{ borderRadius: '14px' }}
            >
              Kapat
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        <MDBox sx={{ display: 'grid', gap: 1.25 }}>
          {Object.entries(grouped).map(([groupName, rows]) => (
            <MDBox
              key={groupName}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '16px',
                background: theme.palette.background.paper,
                overflow: 'hidden',
              }}
            >
              <MDBox
                sx={{
                  px: 1.5,
                  py: 1.1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'rgba(17,24,39,.03)',
                }}
              >
                <MDTypography variant="button" fontWeight="bold" sx={{ lineHeight: 1 }}>
                  {groupName}
                </MDTypography>

                <Chip
                  size="small"
                  variant="outlined"
                  sx={{ height: 24, fontSize: '0.7rem' }}
                  label={`${rows.reduce((a, r) => a + countOn(perms, r), 0)}/${rows.reduce(
                    (a, r) => a + r.scopes.length,
                    0
                  )} açık`}
                />
              </MDBox>

              <MDBox sx={{ px: 1.25, py: 0.6, display: 'grid', gap: 0.75 }}>
                {rows.map((row) => {
                  const on = anyOn(perms, row);
                  const openRow = !!expanded[row.baseKey];

                  return (
                    <MDBox key={row.baseKey} sx={{ borderRadius: '12px' }}>
                      {/* ✅ ana satır */}
                      <MDBox
                        onClick={() => toggleRow(row.baseKey)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          cursor: 'pointer',
                          px: 0.9,
                          py: 0.45,
                          borderRadius: '12px',

                          // 🔽 BURAYA EKLİYORSUN
                          background: on ? 'rgba(22,163,74,.06)' : 'transparent',
                          border: on ? '1px solid rgba(22,163,74,.25)' : '1px solid transparent',

                          '&:hover': { background: 'rgba(17,24,39,.04)' },
                        }}
                      >
                        <MDBox sx={{ minWidth: 0 }}>
                          <MDTypography
                            variant="button"
                            sx={{ fontWeight: 700, lineHeight: 1.05, fontSize: '0.82rem' }}
                          >
                            {row.label}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {countOn(perms, row)}/{row.scopes.length} seçili
                          </MDTypography>
                        </MDBox>

                        <MDBox display="flex" alignItems="center" gap={1}>
                          <Icon sx={{ opacity: 0.6, fontSize: 18 }}>
                            {openRow ? 'expand_less' : 'expand_more'}
                          </Icon>

                          {/* Row switch (tıklama ile aç/kapa) */}
                          <Switch
                            sx={greenSwitchSx}
                            checked={on}
                            onChange={(e) => setRowEnabled(row, e.target.checked)}
                            disabled={loading}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </MDBox>
                      </MDBox>

                      {/* ✅ alt scopes */}
                      <Collapse in={openRow} timeout={180} unmountOnExit>
                        <MDBox
                          sx={{
                            mt: 0.5,
                            ml: 0.25,
                            pl: 0.75,
                            borderLeft: `2px solid ${theme.palette.divider}`,
                            display: 'grid',
                            gap: 0.05,
                          }}
                        >
                          {row.scopes.map((sc) => (
                            <FormControlLabel
                              key={sc.key}
                              sx={{ m: 0, py: 0.15 }}
                              control={
                                <Switch
                                  sx={greenSwitchSx}
                                  checked={!!perms[sc.key]}
                                  onChange={(e) => setPerm(sc.key, e.target.checked)}
                                  disabled={loading}
                                />
                              }
                              label={
                                <MDTypography
                                  variant="button"
                                  sx={{ fontWeight: 600, fontSize: '0.78rem' }}
                                >
                                  {sc.label}
                                </MDTypography>
                              }
                            />
                          ))}
                        </MDBox>
                      </Collapse>
                    </MDBox>
                  );
                })}
              </MDBox>
            </MDBox>
          ))}

          {err ? (
            <MDBox
              sx={{
                p: 1,
                borderRadius: '12px',
                border: `1px solid ${theme.palette.error.main}`,
                background: 'rgba(244,67,54,.06)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Icon sx={{ color: theme.palette.error.main, fontSize: 18 }}>error</Icon>
              <MDTypography
                variant="button"
                sx={{ color: theme.palette.error.main, fontWeight: 700 }}
              >
                {err}
              </MDTypography>
            </MDBox>
          ) : null}
        </MDBox>
      </DialogContent>

      <Divider />

      {saveDisabled && open ? (
        <MDBox sx={{ px: 2, pb: 1 }}>
          <MDBox
            sx={{
              px: 1.1,
              py: 0.85,
              borderRadius: '12px',
              border: `1px dashed ${theme.palette.divider}`,
              background: 'rgba(17,24,39,.04)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
            }}
          >
            <Icon sx={{ fontSize: 18, opacity: 0.75 }}>info</Icon>
            <MDTypography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Kaydet için: <b style={{ color: theme.palette.text.primary }}>{saveBlockReason}</b>
            </MDTypography>
          </MDBox>
        </MDBox>
      ) : null}

      <DialogActions sx={{ p: 2 }}>
        <MDBox display="flex" justifyContent="flex-end" gap={1.2} width="100%">
          <MDButton
            variant="outlined"
            color="dark"
            sx={{ borderRadius: '14px' }}
            onClick={onClose}
            disabled={loading}
          >
            İptal
          </MDButton>

          <Tooltip title={saveDisabled ? saveBlockReason : 'Kaydet'} placement="top" arrow>
            <span>
              <MDButton
                variant="gradient"
                color="success"
                sx={{ borderRadius: '14px' }}
                disabled={saveDisabled}
                onClick={submit}
              >
                <Icon sx={{ mr: 0.5, fontSize: 18 }}>save</Icon>
                Kaydet
              </MDButton>
            </span>
          </Tooltip>
        </MDBox>
      </DialogActions>
    </Dialog>
  );
}
